import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import {
  RefreshCw,
  Download,
  AlertTriangle,
  Clock,
  Sparkles,
  Target,
  CheckSquare,
  Square,
  TrendingUp,
  Award,
  ArrowRight,
  Briefcase,
  GraduationCap,
  ChevronDown,
  ChevronUp,
  Lightbulb,
  Zap,
  MapPin
} from "lucide-react";

const CareerInsights = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState(null);
  const [isCached, setIsCached] = useState(false);
  const [userName, setUserName] = useState("");
  const abortControllerRef = useRef(null);
  
  // Career Path state
  const [careerPath, setCareerPath] = useState(null);
  const [careerPathLoading, setCareerPathLoading] = useState(true);
  const [careerPathExpanded, setCareerPathExpanded] = useState(true);

  useEffect(() => {
    fetchInsights();
    fetchUserName();
    fetchCareerPath();
    
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const fetchUserName = async () => {
    try {
      const response = await fetch(`${API}/users/profile`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setUserName(data.full_name || data.name || user?.name || "You");
      }
    } catch (error) {
      console.error("Failed to fetch user name:", error);
    }
  };

  const fetchCareerPath = async () => {
    setCareerPathLoading(true);
    try {
      const response = await fetch(`${API}/career-paths/personalized`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setCareerPath(data);
      }
    } catch (error) {
      console.error("Failed to fetch career path:", error);
    } finally {
      setCareerPathLoading(false);
    }
  };

  const fetchInsights = async (refresh = false) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    abortControllerRef.current = new AbortController();
    
    if (refresh) setRefreshing(true);
    else setLoading(true);
    setError(null);

    const timeoutId = setTimeout(() => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    }, 90000);

    try {
      const url = refresh ? `${API}/ai/career-insights?refresh=true` : `${API}/ai/career-insights`;
      const response = await fetch(url, {
        credentials: "include",
        signal: abortControllerRef.current.signal,
      });

      clearTimeout(timeoutId);

      if (response.ok) {
        const data = await response.json();
        setInsights(data.insights);
        setIsCached(data.cached || false);
        if (data.cached) {
          toast.info("Showing cached insights. Click refresh for new analysis.");
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        if (response.status === 400 && errorData.detail?.includes("profile")) {
          setError("profile");
        } else if (response.status === 504) {
          setError("timeout");
          toast.error("AI service is busy. Please try again.");
        } else if (response.status === 503) {
          setError("unavailable");
          toast.error("AI service temporarily unavailable.");
        } else {
          setError("generic");
          toast.error(errorData.detail || "Failed to fetch insights");
        }
      }
    } catch (err) {
      clearTimeout(timeoutId);
      if (err.name === 'AbortError') {
        setError("timeout");
        toast.error("Request took too long. Please try again.");
        setLoading(false);
        setRefreshing(false);
        return;
      }
      setError("network");
      toast.error("Network error. Please check your connection.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleDownloadRoadmap = async () => {
    setDownloading(true);
    try {
      const response = await fetch(`${API}/reports/career-roadmap/pdf`, {
        credentials: "include",
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "career_roadmap.pdf";
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success("Career roadmap downloaded!");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to download roadmap");
      }
    } catch (error) {
      toast.error("Download failed");
    } finally {
      setDownloading(false);
    }
  };

  // Parse the markdown into clean sections
  const parseMarkdown = (text) => {
    if (!text) return [];
    
    const sections = [];
    let currentSection = null;
    let currentContent = [];
    
    const lines = text.split('\n');
    
    for (const line of lines) {
      const trimmed = line.trim();
      
      // Main numbered section headers (## 1. Top 3 Career Path, or 1. Top 3 Career Path, 2. Skills, etc.)
      // Handle both "## 1. Title" and "1. Title" formats
      const headerMatch = trimmed.match(/^(?:#{1,3}\s*)?(\d+)\.\s*(.+)$/);
      if (headerMatch && !trimmed.startsWith('**') && trimmed.match(/^(?:#{1,3}\s*)?\d+\./)) {
        // Save previous section
        if (currentSection) {
          sections.push({ ...currentSection, content: currentContent });
        }
        currentSection = {
          number: headerMatch[1],
          title: headerMatch[2].replace(/\*\*/g, '').trim(),
          type: 'section'
        };
        currentContent = [];
        continue;
      }
      
      // Subsection headers (### or **)
      if (trimmed.startsWith('### ') || trimmed.startsWith('## ')) {
        if (currentContent.length > 0 || currentSection) {
          currentContent.push({
            type: 'subheader',
            text: trimmed.replace(/^#+\s*/, '').replace(/\*\*/g, '').trim()
          });
        }
        continue;
      }
      
      // Bold subsection (Path A:, For Path A:, etc.)
      if (trimmed.startsWith('**') || trimmed.match(/^Path [A-C]:/i) || trimmed.match(/^For Path/i)) {
        const cleanText = trimmed.replace(/\*\*/g, '').replace(/:/g, '').trim();
        if (cleanText.length > 2) {
          currentContent.push({
            type: 'subheader',
            text: cleanText
          });
        }
        continue;
      }
      
      // Bullet points
      if (trimmed.startsWith('- ') || trimmed.startsWith('* ') || trimmed.startsWith('• ')) {
        const bulletText = trimmed.replace(/^[-*•]\s*/, '').replace(/\*\*/g, '').trim();
        if (bulletText.length > 0) {
          currentContent.push({
            type: 'bullet',
            text: bulletText
          });
        }
        continue;
      }
      
      // Checkbox items [ ]
      if (trimmed.startsWith('[ ]') || trimmed.startsWith('[x]')) {
        const checked = trimmed.startsWith('[x]');
        const checkText = trimmed.replace(/^\[[ x]\]\s*/, '').replace(/\*\*/g, '').trim();
        if (checkText.length > 0) {
          currentContent.push({
            type: 'checkbox',
            text: checkText,
            checked
          });
        }
        continue;
      }
      
      // Regular paragraph
      if (trimmed.length > 0 && !trimmed.startsWith('#')) {
        const cleanText = trimmed.replace(/\*\*/g, '').trim();
        if (cleanText.length > 5) {
          currentContent.push({
            type: 'paragraph',
            text: cleanText
          });
        }
      }
    }
    
    // Save last section
    if (currentSection) {
      sections.push({ ...currentSection, content: currentContent });
    }
    
    return sections;
  };

  const parsedSections = insights ? parseMarkdown(insights) : [];

  // Show page with Career Path even if insights are loading
  // Only show full-page loading if both career path AND insights are loading
  if (loading && careerPathLoading) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center">
        <div className="text-center max-w-md px-4">
          <div className="w-16 h-16 bg-crimson/20 flex items-center justify-center mx-auto mb-6">
            <Sparkles className="w-8 h-8 text-crimson animate-pulse" />
          </div>
          <h2 className="font-heading text-2xl font-bold mb-2">GENERATING YOUR ROADMAP</h2>
          <p className="text-gray-400 mb-6">Our AI is analyzing your profile and market trends...</p>
          <p className="text-gray-600 text-xs">This typically takes 30-60 seconds</p>
        </div>
      </div>
    );
  }

  // Only show error page if profile is incomplete
  // Otherwise, show page with Career Path section (error shows inline for insights)
  if (error === "profile" && !insights && !careerPath) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center">
        <div className="text-center max-w-md px-4">
          <div className="w-16 h-16 bg-yellow-500/20 flex items-center justify-center mx-auto mb-6">
            <AlertTriangle className="w-8 h-8 text-yellow-500" />
          </div>
          <h2 className="font-heading text-2xl font-bold mb-2">COMPLETE YOUR PROFILE FIRST</h2>
          <p className="text-gray-400 mb-6">Add your skills and experience to get personalized career insights</p>
          <Link to="/profile/build">
            <Button className="bg-crimson hover:bg-crimson-hover">
              BUILD PROFILE
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const today = new Date().toLocaleDateString('en-US', { 
    month: 'long', 
    day: 'numeric', 
    year: 'numeric' 
  });

  return (
    <div className="min-h-screen bg-obsidian" data-testid="career-insights-page">
      <Sidebar />
      
      <main className="lg:ml-64 min-h-screen p-4 sm:p-6 pt-20 lg:pt-8">
        <div className="max-w-3xl mx-auto">
          {/* Clean PDF-style Header */}
          <div className="mb-8 pb-6 border-b border-[#333]">
            <h1 className="font-heading text-3xl sm:text-4xl font-bold mb-2">
              Your Career Roadmap
            </h1>
            <p className="text-gray-400 text-sm">
              Personalized for {userName} • Generated {today}
            </p>
            <p className="text-gray-600 text-xs mt-1">AI-Powered Career Insights</p>
            
            {/* Action buttons */}
            <div className="flex flex-wrap gap-3 mt-6">
              <Button
                onClick={handleDownloadRoadmap}
                disabled={downloading || !insights}
                variant="outline"
                className="border-[#333] text-white hover:border-crimson"
                data-testid="download-roadmap-btn"
              >
                <Download className={`w-4 h-4 mr-2 ${downloading ? "animate-pulse" : ""}`} />
                Download PDF
              </Button>
              <Button
                onClick={() => fetchInsights(true)}
                disabled={refreshing}
                className="bg-crimson hover:bg-crimson-hover"
                data-testid="refresh-btn"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? "animate-spin" : ""}`} />
                Refresh Insights
              </Button>
            </div>
            
            {isCached && (
              <div className="flex items-center gap-2 text-xs text-gray-600 mt-3">
                <Clock className="w-3 h-3" />
                <span>Cached from the last 24 hours</span>
              </div>
            )}
          </div>

          {/* Career Path Progression Section */}
          <div className="mb-8 bg-obsidian-light border border-[#222] overflow-hidden" data-testid="career-path-section">
            <button
              onClick={() => setCareerPathExpanded(!careerPathExpanded)}
              className="w-full flex items-center justify-between p-4 hover:bg-obsidian transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-crimson/20 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-crimson" />
                </div>
                <div className="text-left">
                  <h2 className="font-heading text-lg font-bold">Your Career Path</h2>
                  <p className="text-gray-500 text-xs">
                    {careerPath ? `${careerPath.industry} Industry` : "Personalized progression timeline"}
                  </p>
                </div>
              </div>
              {careerPathExpanded ? (
                <ChevronUp className="w-5 h-5 text-gray-500" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-500" />
              )}
            </button>
            
            {careerPathExpanded && (
              <div className="px-4 pb-6">
                {careerPathLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <RefreshCw className="w-6 h-6 text-crimson animate-spin" />
                    <span className="ml-3 text-gray-400">Analyzing your career trajectory...</span>
                  </div>
                ) : careerPath ? (
                  <div className="space-y-6">
                    {/* AI Personalized Insight */}
                    {careerPath.ai_personalization?.personalized_insight && (
                      <div className="bg-gradient-to-r from-crimson/10 to-transparent border-l-2 border-crimson p-4">
                        <div className="flex items-start gap-3">
                          <Lightbulb className="w-5 h-5 text-crimson flex-shrink-0 mt-0.5" />
                          <p className="text-gray-300 text-sm leading-relaxed">
                            {careerPath.ai_personalization.personalized_insight}
                          </p>
                        </div>
                      </div>
                    )}
                    
                    {/* Career Progression Timeline */}
                    <div className="relative">
                      <h3 className="text-xs uppercase tracking-wider text-gray-500 mb-4 flex items-center gap-2">
                        <MapPin className="w-3 h-3" /> Career Progression
                      </h3>
                      
                      {/* Timeline */}
                      <div className="relative pl-8">
                        {/* Vertical line */}
                        <div className="absolute left-3 top-2 bottom-2 w-0.5 bg-gradient-to-b from-crimson via-crimson/50 to-gray-700"></div>
                        
                        {careerPath.progression?.map((stage, idx) => (
                          <div key={idx} className="relative pb-6 last:pb-0">
                            {/* Dot */}
                            <div className={`absolute -left-5 top-1 w-4 h-4 rounded-full border-2 ${
                              stage.is_current 
                                ? "bg-crimson border-crimson shadow-lg shadow-crimson/50" 
                                : idx === 1 
                                  ? "bg-obsidian border-crimson/70" 
                                  : "bg-obsidian border-gray-600"
                            }`}></div>
                            
                            <div className={`${stage.is_current ? "bg-obsidian border border-crimson/30" : ""} p-3 -ml-2`}>
                              <div className="flex items-center gap-2 mb-1">
                                <span className={`text-xs px-2 py-0.5 ${
                                  stage.is_current 
                                    ? "bg-crimson/20 text-crimson" 
                                    : "bg-gray-800 text-gray-400"
                                }`}>
                                  {stage.level_name}
                                </span>
                                {stage.is_current && (
                                  <span className="text-xs text-green-500">← You are here</span>
                                )}
                              </div>
                              <h4 className="font-semibold text-white">{stage.role}</h4>
                              {stage.typical_roles && stage.typical_roles.length > 0 && !stage.is_current && (
                                <p className="text-gray-500 text-xs mt-1">
                                  Also: {stage.typical_roles.slice(0, 2).join(", ")}
                                </p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    {/* Next Steps */}
                    {careerPath.ai_personalization?.recommended_next_role && (
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div className="bg-obsidian border border-[#333] p-4">
                          <div className="flex items-center gap-2 mb-2">
                            <Target className="w-4 h-4 text-crimson" />
                            <span className="text-xs uppercase tracking-wider text-gray-500">Next Target</span>
                          </div>
                          <p className="font-semibold text-white">{careerPath.ai_personalization.recommended_next_role}</p>
                          <p className="text-gray-500 text-xs mt-1">
                            Est. timeline: {careerPath.ai_personalization.time_to_next_role || "12-24 months"}
                          </p>
                        </div>
                        
                        {careerPath.ai_personalization?.key_skills_to_develop?.length > 0 && (
                          <div className="bg-obsidian border border-[#333] p-4">
                            <div className="flex items-center gap-2 mb-2">
                              <Zap className="w-4 h-4 text-yellow-500" />
                              <span className="text-xs uppercase tracking-wider text-gray-500">Skills to Develop</span>
                            </div>
                            <div className="flex flex-wrap gap-1">
                              {careerPath.ai_personalization.key_skills_to_develop.slice(0, 4).map((skill, idx) => (
                                <span key={idx} className="text-xs bg-yellow-500/10 text-yellow-500 px-2 py-1">
                                  {skill}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Certifications */}
                    {(careerPath.ai_personalization?.recommended_certifications?.length > 0 || 
                      careerPath.template_certifications?.length > 0) && (
                      <div>
                        <h3 className="text-xs uppercase tracking-wider text-gray-500 mb-3 flex items-center gap-2">
                          <Award className="w-3 h-3" /> Recommended Certifications
                        </h3>
                        <div className="flex flex-wrap gap-2">
                          {(careerPath.ai_personalization?.recommended_certifications || careerPath.template_certifications || [])
                            .slice(0, 4)
                            .map((cert, idx) => (
                              <span key={idx} className="text-xs border border-[#333] text-gray-300 px-3 py-1.5">
                                {cert}
                              </span>
                            ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Immediate Actions */}
                    {careerPath.ai_personalization?.immediate_actions?.length > 0 && (
                      <div className="border-t border-[#333] pt-4">
                        <h3 className="text-xs uppercase tracking-wider text-gray-500 mb-3">
                          Immediate Actions
                        </h3>
                        <div className="space-y-2">
                          {careerPath.ai_personalization.immediate_actions.slice(0, 3).map((action, idx) => (
                            <div key={idx} className="flex items-start gap-2">
                              <ArrowRight className="w-4 h-4 text-crimson flex-shrink-0 mt-0.5" />
                              <span className="text-gray-300 text-sm">{action}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Growth Areas */}
                    {careerPath.growth_areas?.length > 0 && (
                      <div className="flex items-center gap-2 text-xs text-gray-500 pt-2">
                        <Briefcase className="w-3 h-3" />
                        <span>Growth areas: {careerPath.growth_areas.slice(0, 3).join(" • ")}</span>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <GraduationCap className="w-10 h-10 text-gray-600 mx-auto mb-3" />
                    <p className="text-gray-400 text-sm">Complete your profile to see your career path</p>
                    <Button 
                      onClick={() => navigate("/profile/build")}
                      className="mt-4 bg-crimson hover:bg-crimson-hover"
                      size="sm"
                    >
                      Build Profile
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Content - Clean readable format matching PDF */}
          {parsedSections.length > 0 ? (
            <div className="space-y-8">
              {parsedSections.map((section, sectionIdx) => (
                <motion.section
                  key={sectionIdx}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: sectionIdx * 0.1 }}
                  className="space-y-4"
                  data-testid={`section-${sectionIdx}`}
                >
                  {/* Section Header */}
                  <h2 className="font-heading text-xl font-bold text-white border-b border-[#333] pb-2">
                    {section.number}. {section.title}
                  </h2>
                  
                  {/* Section Content */}
                  <div className="space-y-3 pl-1">
                    {section.content.map((item, itemIdx) => {
                      if (item.type === 'subheader') {
                        return (
                          <h3 
                            key={itemIdx} 
                            className="font-semibold text-white mt-4 mb-2"
                          >
                            {item.text}
                          </h3>
                        );
                      }
                      
                      if (item.type === 'bullet') {
                        return (
                          <div key={itemIdx} className="flex items-start gap-3">
                            <span className="text-crimson mt-1.5 flex-shrink-0">•</span>
                            <p className="text-gray-300 text-sm leading-relaxed">{item.text}</p>
                          </div>
                        );
                      }
                      
                      if (item.type === 'checkbox') {
                        return (
                          <div key={itemIdx} className="flex items-start gap-3">
                            {item.checked ? (
                              <CheckSquare className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                            ) : (
                              <Square className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" />
                            )}
                            <p className="text-gray-300 text-sm leading-relaxed">{item.text}</p>
                          </div>
                        );
                      }
                      
                      if (item.type === 'paragraph') {
                        return (
                          <p key={itemIdx} className="text-gray-400 text-sm leading-relaxed">
                            {item.text}
                          </p>
                        );
                      }
                      
                      return null;
                    })}
                  </div>
                </motion.section>
              ))}
            </div>
          ) : insights ? (
            // Fallback: show raw text with basic formatting
            <div className="bg-obsidian-light border border-[#222] p-6">
              <div className="prose prose-invert prose-sm max-w-none">
                {insights.split('\n').map((line, i) => {
                  if (!line.trim()) return <br key={i} />;
                  // Strip markdown bold markers
                  const cleanLine = (text) => text.replace(/\*\*/g, '');
                  if (line.startsWith('# ')) {
                    return <h1 key={i} className="text-xl font-bold mt-6 mb-3">{cleanLine(line.replace('# ', ''))}</h1>;
                  }
                  if (line.startsWith('## ')) {
                    return <h2 key={i} className="text-lg font-bold mt-4 mb-2">{cleanLine(line.replace('## ', ''))}</h2>;
                  }
                  if (line.startsWith('### ')) {
                    return <h3 key={i} className="font-semibold mt-3 mb-1">{cleanLine(line.replace('### ', ''))}</h3>;
                  }
                  if (line.startsWith('- ') || line.startsWith('* ')) {
                    return (
                      <div key={i} className="flex gap-2 my-1">
                        <span className="text-crimson">•</span>
                        <span className="text-gray-300 text-sm">{cleanLine(line.replace(/^[-*] /, ''))}</span>
                      </div>
                    );
                  }
                  return <p key={i} className="text-gray-400 text-sm my-1">{cleanLine(line)}</p>;
                })}
              </div>
            </div>
          ) : loading ? (
            // Loading insights (Career Path already showing above)
            <div className="bg-obsidian-light border border-[#222] p-8">
              <div className="flex items-center justify-center gap-4">
                <RefreshCw className="w-6 h-6 text-crimson animate-spin" />
                <p className="text-gray-400">Loading AI career insights...</p>
              </div>
            </div>
          ) : error && error !== "profile" ? (
            // Show retry option if insights failed
            <div className="bg-obsidian-light border border-[#222] p-8 text-center">
              <AlertTriangle className="w-10 h-10 text-yellow-500 mx-auto mb-4" />
              <h3 className="font-heading text-lg font-bold mb-2">AI Insights Temporarily Unavailable</h3>
              <p className="text-gray-400 mb-4 text-sm">
                Your Career Path is shown above. AI-generated detailed insights are loading...
              </p>
              <Button
                onClick={() => fetchInsights(true)}
                disabled={refreshing}
                variant="outline"
                className="border-[#333] text-white hover:border-crimson"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? "animate-spin" : ""}`} />
                Retry Loading Insights
              </Button>
            </div>
          ) : (
            <div className="text-center py-12 bg-obsidian-light border border-[#222]">
              <Target className="w-12 h-12 text-gray-600 mx-auto mb-4" />
              <h3 className="font-heading text-xl font-bold mb-2">No Additional Insights</h3>
              <p className="text-gray-400 mb-6">
                Your Career Path progression is shown above
              </p>
            </div>
          )}

          {/* Footer note */}
          {insights && (
            <div className="mt-10 pt-6 border-t border-[#333]">
              <p className="text-gray-600 text-xs">
                Generated by Rekrutor • {user?.email} • AI-powered career guidance
              </p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default CareerInsights;
